import { ExternalLink } from 'lucide-react';
import { IUser } from '@/redux/auth/auth';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { SOCIAL_PLATFORMS } from './socialPlatforms';

const linkUrl = (user: IUser, key: string) => {
  const legacy: Record<string, string | undefined> = { website: user.website };
  return (
    user.socialLinks?.[key as keyof typeof user.socialLinks] ||
    legacy[key] ||
    ''
  );
};

const toHttps = (raw: string) => {
  const trimmed = raw.trim().replace(/^http:\/\//i, 'https://');
  return /^https:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/** Every link becomes https; anything that can't is dropped (javascript: etc). */
const safeUrl = (raw: string) => {
  try {
    const url = new URL(toHttps(raw));
    return url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
};

const profileLinks = (user: IUser) =>
  SOCIAL_PLATFORMS.map((platform) => ({
    ...platform,
    url: safeUrl(linkUrl(user, platform.key)),
  })).filter((platform) => platform.url);

export default function ProfileLinks({ user }: { user: IUser }) {
  const links = profileLinks(user);
  if (links.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {links.map((link) => (
        <li key={link.key}>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-pill border border-green-200 bg-card px-3 py-1 text-sm font-medium text-green-800 transition-colors hover:border-green-300 hover:bg-green-100 focus-visible:outline-none focus-visible:shadow-focus"
                title={link.url}
              >
                <link.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{link.label}</span>
                <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Open External Link</AlertDialogTitle>
                <AlertDialogDescription className="break-all">
                  {link.url}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Close</AlertDialogCancel>
                <AlertDialogAction asChild>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    Open Link
                  </a>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </li>
      ))}
    </ul>
  );
}
